import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Box, IconButton, MenuItem, Select, Typography } from "@mui/material";
import {
  gridPaginationModelSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { PAGE_SIZE_OPTIONS } from "../utils/utils";
import { CHART_COLORS, CHART_TYPOGRAPHY } from "./app/provider/patient/chart";

const CustomPaginationV2 = () => {
  const apiRef = useGridApiContext();
  const { page, pageSize } = useGridSelector(
    apiRef,
    gridPaginationModelSelector
  );
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);

  const pageCount = pageSize > 0 ? Math.ceil(rowCount / pageSize) : 1;
  const pageSizeOptions = Array.from(
    new Set([...PAGE_SIZE_OPTIONS, pageSize])
  ).sort((a, b) => a - b);

  const textSx = {
    fontFamily: CHART_TYPOGRAPHY.fontFamily,
    fontSize: CHART_TYPOGRAPHY.body4.fontSize,
    lineHeight: "18px",
    color: CHART_COLORS.textDark,
  };

  const navButtonSx = {
    width: 34,
    height: 34,
    bgcolor: CHART_COLORS.bgSecondaryBlue,
    border: `1px solid ${CHART_COLORS.bgSecondaryHover}`,
    borderRadius: "4px",
    "&:hover": { bgcolor: "#d6ecff" },
    "&.Mui-disabled": { opacity: 0.4 },
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "24px",
        px: "16px",
        py: "8px",
        width: "80%",
      }}
    >
      {/* Rows per Page */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <Typography sx={textSx}>Rows per Page</Typography>
        <Typography sx={textSx}>:</Typography>
        <Select
          value={pageSize}
          onChange={(e) => {
            const newSize = Number(e.target.value);
            if (newSize === pageSize) return;
            // `page` and `pageSize` must travel in one update. With a controlled
            // `paginationModel` the grid keeps its own state untouched until the
            // parent re-renders, so a second call (`setPage`) re-reads the stale
            // model and emits the old page size, cancelling the first change.
            apiRef.current.setPaginationModel({
              page: 0,
              pageSize: newSize,
            });
          }}
          size="small"
          sx={{
            height: "34px",
            minWidth: "56px",
            fontFamily: CHART_TYPOGRAPHY.fontFamily,
            ...CHART_TYPOGRAPHY.body5,
            color: CHART_COLORS.textDark,
            bgcolor: CHART_COLORS.inputBg,
            marginTop: "0px",
            borderRadius: "4px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: CHART_COLORS.border,
            },
            "& .MuiSelect-select": {
              py: "6px",
              px: "12px",
            },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Page info + nav buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Typography
            sx={{
              ...textSx,
              fontSize: CHART_TYPOGRAPHY.body5.fontSize,
              fontWeight: CHART_TYPOGRAPHY.weight.medium,
            }}
          >
            {page + 1}
          </Typography>
          <Typography
            sx={{
              ...textSx,
              fontSize: CHART_TYPOGRAPHY.body4.fontSize,
              fontWeight: CHART_TYPOGRAPHY.weight.regular,
            }}
          >
            of {pageCount || 1}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <IconButton
            size="small"
            disabled={page === 0}
            onClick={() => apiRef.current.setPage(0)}
            sx={navButtonSx}
          >
            <FirstPageIcon sx={{ fontSize: 18, color: CHART_COLORS.primary }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page === 0}
            onClick={() => apiRef.current.setPage(page - 1)}
            sx={navButtonSx}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: CHART_COLORS.primary }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= pageCount - 1}
            onClick={() => apiRef.current.setPage(page + 1)}
            sx={navButtonSx}
          >
            <ArrowForwardIcon
              sx={{ fontSize: 18, color: CHART_COLORS.primary }}
            />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= pageCount - 1}
            onClick={() => apiRef.current.setPage(pageCount - 1)}
            sx={navButtonSx}
          >
            <LastPageIcon sx={{ fontSize: 18, color: CHART_COLORS.primary }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomPaginationV2;
